/**
 * TrafficLightBanner
 *
 * Displays a quick-glance verdict banner at the top of IntelliHealth recommendations.
 * 🟢 Approved — no issues found
 * 🟡 Approved with caveats — follow-up actions required
 * 🔴 Blocked — safety concern or contraindication
 *
 * Phase 1: Infers verdict from SOAP assessment/plan text via keyword detection.
 * Phase 2: Will receive structured verdict from aura-ai backend.
 */

import React from 'react';
import { CheckCircle2, AlertTriangle, XCircle, Shield } from 'lucide-react';

// ============================================================================
// Types
// ============================================================================

export type VerdictLevel = 'approved' | 'caution' | 'blocked';

export interface TrafficLightVerdict {
  level: VerdictLevel;
  headline: string;
  actionCount: number;
  urgentCount: number;
}

// ============================================================================
// Verdict Inference (Phase 1 — keyword-based)
// ============================================================================

const BLOCKED_KEYWORDS = [
  'contraindicated',
  'do not prescribe',
  'absolute contraindication',
  'discontinue immediately',
  'hy\'s law criteria met',
  'safety concern identified',
  'blocked',
  'cannot proceed',
];

const URGENT_KEYWORDS = [
  'urgent',
  'immediate',
  'critical',
  'stat',
  'emergent',
  'safety-critical',
];

const CAUTION_KEYWORDS = [
  'follow-up',
  'follow up',
  'monitor',
  'recommend',
  'verify',
  'confirm',
  'order',
  'action required',
  'action needed',
  'clinician should',
  'reassess',
];

/**
 * Infers a traffic light verdict from SOAP text content.
 * Phase 2 will replace this with structured backend data.
 */
export function inferVerdict(
  assessment?: string | null,
  plan?: string | null,
  summary?: string | null,
): TrafficLightVerdict {
  const fullText = [assessment, plan, summary].filter(Boolean).join(' ').toLowerCase();

  // Check for blocked conditions
  const isBlocked = BLOCKED_KEYWORDS.some((kw) => fullText.includes(kw));
  if (isBlocked) {
    return {
      level: 'blocked',
      headline: 'Safety concern identified — review required before proceeding',
      actionCount: 0,
      urgentCount: 1,
    };
  }

  // Count urgent items
  const urgentCount = URGENT_KEYWORDS.reduce((count, kw) => {
    const regex = new RegExp(kw, 'gi');
    const matches = fullText.match(regex);
    return count + (matches ? matches.length : 0);
  }, 0);
  // Deduplicate — cap at reasonable number
  const dedupedUrgent = Math.min(urgentCount, 5);

  // Count action items from plan
  const planLines = (plan || '').split('\n').filter((line) =>
    CAUTION_KEYWORDS.some((kw) => line.toLowerCase().includes(kw))
  );
  const actionCount = Math.max(planLines.length, dedupedUrgent);

  if (dedupedUrgent > 0 || actionCount > 0) {
    const urgentSuffix = dedupedUrgent > 0 ? ` (${dedupedUrgent} urgent)` : '';
    return {
      level: 'caution',
      headline: `Approved — ${actionCount} follow-up action${actionCount !== 1 ? 's' : ''} required${urgentSuffix}`,
      actionCount,
      urgentCount: dedupedUrgent,
    };
  }

  return {
    level: 'approved',
    headline: 'Approved — no follow-up actions required',
    actionCount: 0,
    urgentCount: 0,
  };
}

// ============================================================================
// Component
// ============================================================================

const VERDICT_STYLES: Record<VerdictLevel, {
  bg: string;
  border: string;
  icon: React.ElementType;
  iconColor: string;
  textColor: string;
}> = {
  approved: {
    bg: 'bg-emerald-50',
    border: 'border-emerald-200',
    icon: CheckCircle2,
    iconColor: 'text-emerald-500',
    textColor: 'text-emerald-800',
  },
  caution: {
    bg: 'bg-amber-50',
    border: 'border-amber-200',
    icon: AlertTriangle,
    iconColor: 'text-amber-500',
    textColor: 'text-amber-800',
  },
  blocked: {
    bg: 'bg-red-50',
    border: 'border-red-200',
    icon: XCircle,
    iconColor: 'text-red-500',
    textColor: 'text-red-800',
  },
};

interface TrafficLightBannerProps {
  verdict: TrafficLightVerdict;
}

const TrafficLightBanner: React.FC<TrafficLightBannerProps> = ({ verdict }) => {
  const style = VERDICT_STYLES[verdict.level];
  const Icon = style.icon;

  return (
    <div className={`${style.bg} ${style.border} border rounded-xl px-4 py-3 flex items-center gap-3`}>
      <div className="flex items-center gap-2 flex-shrink-0">
        <Shield className="h-4 w-4 text-[hsl(var(--ih-text-muted))]" />
        <Icon className={`h-5 w-5 ${style.iconColor}`} />
      </div>
      <p className={`${style.textColor} text-sm font-medium`}>
        {verdict.headline}
      </p>
    </div>
  );
};

export default TrafficLightBanner;
